import Vue from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import ObjectPath from "object-path";
import router from "@/router";
import JwtService from "@/service/jwt.service";
import { ErrorEventBus } from "@/lib/message.lib";

/**
 * Service to call HTTP request via Axios
 */
const ApiService = {
  init() {
    Vue.use(VueAxios, axios);
    Vue.axios.defaults.baseURL = process.env.VUE_APP_API_URL;
    Vue.axios.defaults.headers.common["Accept"] = "application/json";
    Vue.axios.defaults.trailingSlash = false;

    // Add a request interceptor
    Vue.axios.interceptors.request.use(
      function (config) {
        if (config.url.endsWith("/")) {
          config.url = config.url.slice(0, -1);
        }
        return config;
      },
      function (error) {
        return Promise.reject(error);
      }
    );

    // Add a response interceptor
    Vue.axios.interceptors.response.use(
      function (response) {
        return response;
      },
      function (error) {
        if (error.code == "ERR_NETWORK") {
          ErrorEventBus.$emit("set-form-error", [error.message]);
        } else {
          ObjectPath.del(error, "response.data.debug");
          const status = ObjectPath.get(error, "response.status");
          const response = ObjectPath.get(error, "response.data");
          if (status == 422) {
            ErrorEventBus.$emit("set-form-error", response);
          }
          if (status == 401) {
            JwtService.destroyAuth();
            router.push({ name: "login" });
          }
        }
        return Promise.reject(error);
      }
    );
  },

  /**
   * Set the default HTTP request headers
   */
  setHeader() {
    return new Promise((resolve, reject) => {
      JwtService.getToken()
        .then((token) => {
          Vue.axios.defaults.headers.common[
            "Authorization"
          ] = `Bearer ${token}`;
          resolve(token);
        })
        .catch((error) => {
          reject(error);
          JwtService.destroyAuth();
        });
    });
  },

  /**
   * Send the QUERY HTTP request
   * @param resource
   * @param slug
   * @returns {*}
   */
  query(resource, params) {
    return new Promise((resolve, reject) => {
      Vue.axios
        .get(resource, { params: params })
        .then((response) => {
          const output = ObjectPath.get(response, "data.data");
          resolve(output);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  /**
   * Send the GET HTTP request
   * @param resource
   * @param slug
   * @returns {*}
   */
  get(resource, slug = "") {
    return new Promise((resolve, reject) => {
      Vue.axios
        .get(`${resource}/${slug}`)
        .then((response) => {
          const output = ObjectPath.get(response, "data.data");
          resolve(output);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  /**
   * Set the POST HTTP request
   * @param resource
   * @param params
   * @returns {*}
   */
  post(resource, params) {
    return new Promise((resolve, reject) => {
      Vue.axios
        .post(resource, params)
        .then((response) => {
          const output = ObjectPath.get(response, "data.data");
          resolve(output);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  /**
   * Send the UPDATE HTTP request
   * @param resource
   * @param slug
   * @param params
   * @returns {IDBRequest<IDBValidKey> | Promise<void>}
   */
  update(resource, slug, params) {
    return new Promise((resolve, reject) => {
      Vue.axios
        .put(`${resource}/${slug}`, params)
        .then((response) => {
          const output = ObjectPath.get(response, "data.data");
          resolve(output);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  /**
   * Send the PUT HTTP request
   * @param resource
   * @param params
   * @returns {IDBRequest<IDBValidKey> | Promise<void>}
   */
  put(resource, params) {
    return new Promise((resolve, reject) => {
      Vue.axios
        .put(resource, params)
        .then((response) => {
          const output = ObjectPath.get(response, "data.data");
          resolve(output);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  /**
   * Send the PATCH HTTP request
   * @param resource
   * @param params
   * @returns {IDBRequest<IDBValidKey> | Promise<void>}
   */
  patch(resource, params) {
    return new Promise((resolve, reject) => {
      Vue.axios
        .patch(resource, params)
        .then((response) => {
          const output = ObjectPath.get(response, "data.data");
          resolve(output);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  /**
   * Send the DELETE HTTP request
   * @param resource
   * @returns {*}
   */
  delete(resource) {
    return new Promise((resolve, reject) => {
      Vue.axios
        .delete(resource)
        .then((response) => {
          const output = ObjectPath.get(response, "data.data");
          resolve(output);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },

  /**
   * Send the UPLOAD HTTP request
   * @param resource
   * @returns {*}
   */
  upload(resource, params) {
    return new Promise((resolve, reject) => {
      Vue.axios
        .post(resource, params, {
          headers: { "content-type": "multipart/form-data" },
        })
        .then((response) => {
          const output = ObjectPath.get(response, "data.data");
          resolve(output);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
};

export default ApiService;
